/**
 * Arena combat + the gold/greed mechanic (spec §11). The banking tension is the core
 * of the mode — kills bank nothing until you walk back to the safe space, and dying
 * with unbanked gold costs half. These rules are pure so the sim and the live match
 * share exactly one source of truth, and the difficulty harness can verify them.
 */

export const ARENA = {
  // Gold / streak.
  KILL_GOLD: 25,
  STREAK_MULT: 1.15,        // ×1.15 per consecutive kill without returning
  DEATH_LOSS: 0.5,          // dying with unbanked gold loses this fraction
  SAFE_COOLDOWN_MS: 15_000, // after entering the safe space you can't leave for 15s
  // NO REGEN. The sanctuary used to heal you to full, which made the middle of the
  // map the only place worth being — owner, 2026-09-02: "let's exclude health
  // regeneration on safe zone, too much movement around the safe zone." It is now a
  // BANK and a SHIELD: entering banks every pending coin and resets the streak, and
  // while it is open nobody can hit you and you cannot hit out. HP never comes back.
  SAFE_CAMP_MAX_MS: 30_000, // stand in the safe space >30s during the fight → kicked out
  SAFE_CLOSE_LAST_MS: 60_000, // the safe space CLOSES 1 minute before the end (owner)
  // Hazards.
  LAVA_DPS: 15,
  GEYSER_DMG: 25,
  GEYSER_PERIOD_MS: 20_000,
  GEYSER_TELEGRAPH_MS: 1_500,
  SHROOM_SLOW: 0.4,          // −40% move speed in a shroom cloud
  // Match shape.
  BATTLE_MS: 600_000,       // 10 minutes
  SHRINK_LAST_MS: 120_000,  // final 2 minutes the map closes in
} as const;

/** Gold a kill is worth at a given streak (0 = first kill of the run). */
export function killGold(streak: number): number {
  return Math.floor(ARENA.KILL_GOLD * Math.pow(ARENA.STREAK_MULT, Math.max(0, streak)));
}

/** Banking on safe-space entry: everything pending is banked, streak resets. */
export function bankGold(pending: number): { banked: number; pending: 0; streak: 0 } {
  return { banked: Math.floor(pending), pending: 0, streak: 0 };
}

/** Dying with unbanked gold: this much is lost (the rest… also gone — it never banked). */
export function deathLoss(pending: number): number {
  return Math.floor(pending * ARENA.DEATH_LOSS);
}

// Per-tier combat stats (spec §Bots — Rookies miss, Elites are accurate/fast).
export interface CombatStats {
  damage: number;       // per hit
  range: number;        // tiles
  cooldownMs: number;   // between swings
  missChance: number;   // fraction of swings that whiff
  reactionMs: number;   // (used by perception)
}

export const TIER_COMBAT: Record<string, CombatStats> = {
  rookie: { damage: 8, range: 1.2, cooldownMs: 900, missChance: 0.25, reactionMs: 550 },
  regular: { damage: 10, range: 1.3, cooldownMs: 800, missChance: 0.12, reactionMs: 320 },
  veteran: { damage: 12, range: 1.4, cooldownMs: 700, missChance: 0.06, reactionMs: 200 },
  elite: { damage: 14, range: 1.5, cooldownMs: 600, missChance: 0.03, reactionMs: 140 },
};

/**
 * Does this swing land?
 *
 * Before this there was one flat number per tier and nothing else: an Elite whiffed
 * exactly 3% of swings whether the target was standing still or sprinting past, and
 * whether the swinger was at full health or on one hit point. "No perfect aim" in the
 * charter means aim that DEGRADES — under pressure, at the edge of reach, against
 * something moving — and differs by personality.
 *
 * Shared by the headless sim and the live match so the harness measures the player's
 * actual opponents. Pure: the caller supplies the roll.
 */
export interface AimContext {
  /** The target moved since the last tick — a harder swing to time. */
  targetMoving: boolean;
  /** Attacker's remaining health, 0..1. Hurt fighters swing worse. */
  selfHpPct: number;
  /** Distance and reach, in tiles: a swing at the very edge is a worse swing. */
  dist: number;
  range: number;
}

/** Never below this, never above it — a bot must always be able to hit and to miss. */
const MISS_FLOOR = 0.02;
const MISS_CEIL = 0.55;

export function rollHit(
  p: { missChance: number; aimError?: number },
  ctx: AimContext,
  r: number,
): boolean {
  const wild = p.aimError ?? 1;
  let miss = p.missChance * wild;
  if (ctx.targetMoving) miss += 0.08 * wild;
  miss += 0.18 * Math.max(0, 1 - ctx.selfHpPct) * wild;                 // wounded → shaky
  miss += 0.10 * Math.min(1, ctx.dist / Math.max(0.01, ctx.range)) * wild;  // edge of reach
  return r >= Math.min(MISS_CEIL, Math.max(MISS_FLOOR, miss));
}

/** Effective fighting power for the 1v1 "press or stall" decision (spec §6 row 12). */
export function combatPower(hp: number, stats: CombatStats): number {
  return hp * (stats.damage / stats.cooldownMs) * (1 - stats.missChance);
}

// ─── Crafted gear in the arena ───────────────────────────────────────────────
//
// OWNER: "If i have armor, i should appear in arena with armor which i have" and,
// asked whether gear should affect arena STATS: "2. crafted gear affect arena".
//
// Gear used to be cosmetic here — the arena read tier profiles (rookie..elite) and
// nothing else, so the ruby set a player spent the whole ladder on changed nothing
// once he stepped through the gate. It does now.
//
// Expressed as PERCENTAGES, deliberately, and not by reusing the farm's flat
// numbers. `crafting/gear.ts weaponDamage` is tier×3 — up to +27 — against arena
// base damage of 8–14, so a ruby sword would have roughly tripled every swing; and
// `armorDefense` soaks up to 20 flat, which is more than an elite's 14 and would
// have made a fully-armoured player literally unkillable. The farm's numbers are
// right for the farm, where a night mob hits for a handful; the arena is a
// different fight and needs its own scale. What carries over is the SHAPE — a
// weapon adds, armour subtracts, and both are capped.
//
// The caps are the point. A 10-minute battle royale that the best-geared player
// wins by default is not a mode, it is a rank list — and the difficulty harness
// targets a 3–6% top-1 rate for an average player, which a dominant gear bonus
// would blow straight through. So: a real edge, never a decided outcome.

/** The tiers the arena reads off a fighter. Farm slots; 0 = nothing in that slot. */
export interface ArenaGear {
  weapon: number;
  helmet: number;
  chest: number;
  legs: number;
}

export const NO_GEAR: ArenaGear = { weapon: 0, helmet: 0, chest: 0, legs: 0 };

/** Per-tier attack bonus, and the cap it reaches at the top of the ladder. */
export const GEAR_ATTACK_PER_TIER = 0.03;   // +3% damage per weapon tier
export const GEAR_ATTACK_MAX = 0.27;        // ruby (tier 9) = +27%
/** Per-point mitigation, and its cap. */
export const GEAR_SOAK_PER_POINT = 0.02;    // −2% damage taken per defence point
export const GEAR_SOAK_MAX = 0.30;          // never more than −30%

/**
 * Armour defence points — the same weighting the farm uses (chest counts double,
 * it is the big piece), so a player's set ranks the same way in both places.
 */
export function arenaDefense(g: ArenaGear): number {
  return Math.max(0, g.helmet) + Math.max(0, g.chest) * 2 + Math.max(0, g.legs);
}

/** Multiplier on damage this fighter DEALS. 1.0 with nothing equipped. */
export function attackMult(g: ArenaGear): number {
  return 1 + Math.min(GEAR_ATTACK_MAX, Math.max(0, g.weapon) * GEAR_ATTACK_PER_TIER);
}

/** Multiplier on damage this fighter TAKES. 1.0 bare, 0.7 at the cap. */
export function damageTakenMult(g: ArenaGear): number {
  return 1 - Math.min(GEAR_SOAK_MAX, arenaDefense(g) * GEAR_SOAK_PER_POINT);
}

/**
 * What one landed swing actually removes, attacker's weapon and defender's armour
 * both counted. Rounded, and never below 1 — a fully-armoured target must still be
 * killable by a bare-handed one, or the cap above is a loophole rather than a limit.
 */
export function gearedDamage(base: number, attacker: ArenaGear, defender: ArenaGear): number {
  return Math.max(1, Math.round(base * attackMult(attacker) * damageTakenMult(defender)));
}

/**
 * The gear a bot shows up in.
 *
 * Bots already rolled cosmetic plate at 45% in the live match; now that gear
 * CHANGES a fight, that roll has to be a real loadout and the same one has to
 * dress the sprite, or the arena would show armour that does nothing and hide
 * armour that does. Tiers stay modest — a mid-ladder field, not a room full of
 * ruby — so a player who has climbed is rewarded without the bots' rolls deciding
 * the match between themselves.
 */
export function rollBotGear(rng: () => number): ArenaGear {
  const piece = (chance: number, top: number) => (rng() < chance ? 1 + Math.floor(rng() * top) : 0);
  return {
    weapon: piece(0.75, 5),   // most carry something; up to Gold
    helmet: piece(0.45, 4),
    chest: piece(0.45, 4),
    legs: piece(0.35, 3),
  };
}

/**
 * Can this swing land? The ONE answer, shared by the player's target picker and the
 * melee resolution in the match view.
 *
 * It was written out twice, inline, in two places that had drifted apart — and both
 * measured distance in a tile space that disagreed with the one the heroes were
 * actually standing in (see arena/coords.ts). "Some players are unattackable, damage
 * never applies even in range" was that disagreement plus the sanctuary rule, which
 * nothing on screen explained. Distances are in TILES, foot-anchored.
 */
export function canStrike(opts: {
  /** Attacker's foot tile. */
  from: { tx: number; ty: number };
  /** Target's foot tile. */
  to: { tx: number; ty: number };
  /** The attacker's reach, in tiles (TIER_COMBAT range). */
  range: number;
  /** Either fighter standing on the sanctuary stone while it is still open. */
  attackerProtected: boolean;
  targetProtected: boolean;
}): boolean {
  if (opts.attackerProtected || opts.targetProtected) return false;
  // Inclusive: a target exactly at the range boundary is IN range. An exclusive test
  // here is a whole class of "I was right next to him and nothing happened".
  return Math.hypot(opts.to.tx - opts.from.tx, opts.to.ty - opts.from.ty) <= opts.range;
}
